import { useState, useEffect } from "react";

// ui
import {
  Typography,
  Box,
  Paper,
  Alert,
  FormControl,
  TextField,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/system";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { PoButton } from "./common/PoButton";
import { PoModalShell } from "./common/PoModalShell";

// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

// API
import { postPaymentConfirmExcelRecords } from "../api/paymentConfirmExcelRecord";
import {
  postPaymentConfirmExcel,
  postPaymentConfirmExcelMail,
} from "../api/paymentConfirmExcel";
import { getSerchSaleCompanyUniquePayments } from "../api/serch";

const CustomTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    fontSize: "12px",
    height: "12px", // ここでフォントサイズを指定
  },
});
CustomTextField.defaultProps = {
  autoComplete: "off",
};

interface GetXLSXPaymentModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  getXLSXStatus: {
    status:
      | "none"
      | "open"
      | "getting"
      | "confirm"
      | "error_confirm"
      | "send_mail"
      | "error_send_mail"
      | "result"
      | "loading"
      | "error";
    message: string;
    sale_company_id: number | null;
    sale_company_name: string;
    payment_year: string;
    payment_month: string;
    payment_day: string;
    saleCompanyUniquePaymentIDs: string[];
    saleCompanyUniquePayments: {
      id: string;
      sale_company_unique_payment_name: string;
      sale_company_unique_payment_value: string;
    }[];
    destination_key: string;
    confirm_destination_key: string;
    payment_target: "partner_payment" | "high_rank_payment";
  };
  setGetXLSXStatus: (getXLSXStatus: {
    status:
      | "none"
      | "open"
      | "getting"
      | "confirm"
      | "error_confirm"
      | "send_mail"
      | "error_send_mail"
      | "result"
      | "loading"
      | "error";
    message: string;
    sale_company_id: number | null;
    sale_company_name: string;
    payment_year: string;
    payment_month: string;
    payment_day: string;
    saleCompanyUniquePaymentIDs: string[];
    saleCompanyUniquePayments: {
      id: string;
      sale_company_unique_payment_name: string;
      sale_company_unique_payment_value: string;
    }[];
    destination_key: string;
    confirm_destination_key: string;
    payment_target: "partner_payment" | "high_rank_payment";
  }) => void;
  saleCompanyUniquePayments: {
    id: string;
    sale_company_unique_payment_name: string;
    sale_company_unique_payment_value: string;
  }[];
  setSaleCompanyUniquePayments: (
    value: {
      id: string;
      sale_company_unique_payment_name: string;
      sale_company_unique_payment_value: string;
    }[]
  ) => void;
  getXLSXFunc: () => void;
  firstYear: number;
  thisYear: number;
  thisMonth: number;
  companyList: { id: number; sale_company_name: string }[];
  confirmHighRankPaymentRecords: () => void;
}

export function GetXLSXPaymentModal(props: GetXLSXPaymentModalProps) {
  const {
    onModal,
    swCloseModal,
    getXLSXStatus,
    setGetXLSXStatus,
    getXLSXFunc,
    saleCompanyUniquePayments,
    setSaleCompanyUniquePayments,
    firstYear,
    thisYear,
    thisMonth,
    companyList,
    confirmHighRankPaymentRecords,
  } = props;

  const loginUser = useAtomValue(AppAtoms.loginUser);

  const createDayList = () => {
    let dayList = [];
    for (let i = 20; i <= 31; i++) {
      dayList.push(i.toString());
    }
    return dayList;
  };
  const createMonthList = () => {
    let monthList = [];
    for (let i = 1; i <= 12; i++) {
      monthList.push(i.toString());
    }
    return monthList;
  };
  const createYearList = () => {
    let yearList = [];
    for (let i = firstYear; i <= thisYear; i++) {
      yearList.push(i.toString());
    }
    return yearList;
  };
  const footerNode =
    getXLSXStatus.status === "open" ? (
      <>
        {getXLSXStatus.payment_target == "partner_payment" ? (
          <PoButton
            data-testid="payment-xlsx-partner-export"
            variant="secondary"
            disabled={
              loginUser?.authority !== "パートナー" &&
              getXLSXStatus.sale_company_id === null
            }
            onClick={getXLSXFunc}
          >
            <Box>パートナー明細出力</Box>
            <FileDownloadIcon sx={{ marginLeft: "6px" }} fontSize="medium" />
          </PoButton>
        ) : (
          <PoButton
            data-testid="payment-xlsx-record-confirm"
            variant="secondary"
            onClick={confirmHighRankPaymentRecords}
          >
            <Box>レコード確定</Box>
          </PoButton>
        )}
        <PoButton
          data-testid="payment-xlsx-cancel"
          variant="secondary"
          onClick={swCloseModal}
        >
          <Box>キャンセル</Box>
        </PoButton>
      </>
    ) : getXLSXStatus.status === "error" ? (
      <PoButton
        data-testid="payment-xlsx-error-back"
        variant="secondary"
        onClick={() =>
          setGetXLSXStatus({
            ...getXLSXStatus,
            status: "open",
          })
        }
      >
        戻る
      </PoButton>
    ) : getXLSXStatus.status === "confirm" ? (
      <>
        <PoButton
          data-testid="payment-xlsx-confirm-submit"
          variant="secondary"
          onClick={async () => {
            setGetXLSXStatus({
              ...getXLSXStatus,
              status: "loading",
            });
            const responce = await postPaymentConfirmExcelRecords({
              sale_company_id: getXLSXStatus.sale_company_id,
              payment_year: getXLSXStatus.payment_year,
              payment_month: getXLSXStatus.payment_month,
              confirm_destination_key:
                getXLSXStatus.confirm_destination_key,
              sale_company_name: getXLSXStatus.sale_company_name,
              saleCompanyUniquePayments:
                getXLSXStatus.saleCompanyUniquePayments,
            });
            if (responce == "success") {
              const xlsx_res = await postPaymentConfirmExcel({
                destination_key: getXLSXStatus.destination_key,
                confirm_destination_key:
                  getXLSXStatus.confirm_destination_key,
              });
              if (xlsx_res == "success") {
                setGetXLSXStatus({
                  ...getXLSXStatus,
                  status: "send_mail",
                });
              } else {
                setGetXLSXStatus({
                  ...getXLSXStatus,
                  status: "error_confirm",
                });
              }
            } else {
              setGetXLSXStatus({
                ...getXLSXStatus,
                status: "error_confirm",
              });
            }
          }}
        >
          <Box>確定</Box>
        </PoButton>
        <PoButton
          data-testid="payment-xlsx-confirm-close"
          variant="secondary"
          onClick={swCloseModal}
        >
          閉じる
        </PoButton>
      </>
    ) : getXLSXStatus.status === "error_confirm" ? (
      <PoButton
        data-testid="payment-xlsx-error-confirm-back"
        variant="secondary"
        onClick={() =>
          setGetXLSXStatus({
            ...getXLSXStatus,
            status: "open",
            message: "",
          })
        }
      >
        戻る
      </PoButton>
    ) : getXLSXStatus.status === "send_mail" ? (
      <>
        <PoButton
          variant="secondary"
          data-testid="payment-xlsx-send-mail"
          onClick={async () => {
            setGetXLSXStatus({
              ...getXLSXStatus,
              status: "loading",
            });
            const responce = await postPaymentConfirmExcelMail({
              payment_year: getXLSXStatus.payment_year,
              payment_month: getXLSXStatus.payment_month,
              payment_sale_company_id: getXLSXStatus.sale_company_id,
            });
            if (responce == "success") {
              setGetXLSXStatus({
                ...getXLSXStatus,
                message: "通知メールを送信しました。",
                status: "result",
              });
            } else {
              setGetXLSXStatus({
                ...getXLSXStatus,
                status: "error_send_mail",
              });
            }
          }}
        >
          <Box>送信</Box>
        </PoButton>
        <PoButton
          data-testid="payment-xlsx-send-mail-close"
          variant="secondary"
          onClick={swCloseModal}
        >
          閉じる
        </PoButton>
      </>
    ) : getXLSXStatus.status === "error_send_mail" ? (
      <PoButton
        data-testid="payment-xlsx-error-mail-back"
        variant="secondary"
        onClick={() =>
          setGetXLSXStatus({
            ...getXLSXStatus,
            status: "open",
            message: "",
          })
        }
      >
        戻る
      </PoButton>
    ) : getXLSXStatus.status === "result" ? (
      <PoButton
        data-testid="payment-xlsx-result-close"
        variant="secondary"
        onClick={swCloseModal}
      >
        閉じる
      </PoButton>
    ) : undefined;
  const [postCSVSaleCompanyName, setPostCSVSaleCompanyName] =
    useState<string>("");
  const autocompleteSaleCompanyList: string[] = companyList.map(
    (saleCompany) => {
      return saleCompany.sale_company_name;
    }
  );
  useEffect(() => {
    async function setSaleCompany() {
      let saleCompanyID: null | number = null;
      let saleCompanyName: string = "";
      companyList.map((saleCompany) => {
        if (postCSVSaleCompanyName === saleCompany.sale_company_name) {
          saleCompanyID = saleCompany.id;
          saleCompanyName = saleCompany.sale_company_name;
        }
      });
      if (saleCompanyID) {
        setGetXLSXStatus({
          ...getXLSXStatus,
          status: "loading",
          sale_company_id: saleCompanyID,
          sale_company_name: saleCompanyName,
        });
        const saleCompanyUniquePayments =
          await getSerchSaleCompanyUniquePayments(saleCompanyID);
        if (saleCompanyUniquePayments) {
          setSaleCompanyUniquePayments(
            saleCompanyUniquePayments.saleCompanyUniquePayments
          );
        }
        setGetXLSXStatus({
          ...getXLSXStatus,
          status: "open",
          sale_company_id: saleCompanyID,
          sale_company_name: saleCompanyName,
          saleCompanyUniquePayments: [],
          destination_key: "",
        });
      }
    }
    setSaleCompany();
  }, [postCSVSaleCompanyName]);
  return (
    <PoModalShell
      open={onModal}
      title="明細作成・確定"
      onClose={swCloseModal}
      width="w760"
      testidPrefix="payment-xlsx"
      frameTestId="payment-xlsx-modal"
      footer={footerNode}
    >
        {getXLSXStatus.status === "open" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Paper elevation={1} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <Box>
                <Box key={"paymentElsxAlert"} sx={{ margin: "0px 0px" }}>
                  <Alert severity="info">
                    {getXLSXStatus.payment_target == "partner_payment" ? (
                      <Box>
                        確認フラグが「ON」となっているレコードのみ出力されます。
                      </Box>
                    ) : (
                      <Box>
                        確認フラグが「ON」となっているレコードのみ確定済とします。
                        <br />
                        「上位店の支払のみのレコード」では、支払先パートナーが存在しないレコードを確定済とします。
                        <br />
                        　例：上位店の支払はあるが、パートナーへの振込がない時
                      </Box>
                    )}
                  </Alert>
                </Box>
                <Box
                  key={"paymentElsxOutputObject"}
                  sx={{ margin: "30px 0px", display: "flex", flexDirection: "column", gap: "var(--sp-1)", paddingLeft: "30px", alignItems: "flex-start" }}
                >
                  <FormControl className="po-label">
                    出力対象
                  </FormControl>
                  <FormControl sx={{ width: "380px" }}>
                    <Select
                      labelId="serch-input-label"
                      id="serch-input"
                      value={getXLSXStatus.payment_target}
                      onChange={(e) =>
                        setGetXLSXStatus({
                          ...getXLSXStatus,
                          payment_target: e.target.value as
                            | "partner_payment"
                            | "high_rank_payment",
                        })
                      }
                      sx={{
                        height: "30px",
                        textAlign: "end",
                      }}
                    >
                      <MenuItem
                        key={"inputpartner"}
                        value={"partner_payment"}
                        sx={{ textAlign: "right" }}
                      >
                        パートナー支払明細
                      </MenuItem>
                      <MenuItem
                        key={"inputHighRank"}
                        value={"high_rank_payment"}
                        sx={{ textAlign: "right" }}
                      >
                        上位店の支払のみのレコード
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box key={"paymentElsxBoxYear"} sx={{ margin: "30px 0px", display: "flex", flexDirection: "column", gap: "var(--sp-1)", paddingLeft: "30px", alignItems: "flex-start" }}>
                  <FormControl className="po-label">
                    明細年月
                  </FormControl>
                  <Box sx={{ display: "flex", gap: "16px" }}>
                    <FormControl sx={{ width: "150px" }}>
                      <InputLabel id="serch-input-label">年</InputLabel>
                      <Select
                        labelId="serch-input-label"
                        id="serch-input"
                        value={getXLSXStatus.payment_year}
                        label={"年"}
                        onChange={(e) =>
                          setGetXLSXStatus({
                            ...getXLSXStatus,
                            payment_year: e.target.value,
                          })
                        }
                        sx={{
                          height: "30px",
                          textAlign: "end",
                        }}
                      >
                        {createYearList().map((input, i) => {
                          return (
                            <MenuItem
                              key={"input" + i}
                              value={input}
                              sx={{ textAlign: "right" }}
                            >
                              {input}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ width: "100px" }}>
                      <InputLabel id="serch-input-label">月</InputLabel>
                      <Select
                        labelId="serch-input-label"
                        id="serch-input"
                        value={getXLSXStatus.payment_month}
                        label={"月"}
                        onChange={(e) =>
                          setGetXLSXStatus({
                            ...getXLSXStatus,
                            payment_month: e.target.value,
                          })
                        }
                        sx={{
                          height: "30px",
                          textAlign: "end",
                        }}
                      >
                        {createMonthList().map((input, i) => {
                          return (
                            <MenuItem
                              key={"input" + i}
                              value={input}
                              sx={{ textAlign: "right" }}
                            >
                              {input}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                {getXLSXStatus.payment_target == "partner_payment" ? (
                  <>
                    <Box key={"paymentElsxBoxYear"} sx={{ margin: "30px 0px", display: "flex", flexDirection: "column", gap: "var(--sp-1)", paddingLeft: "30px", alignItems: "flex-start" }}>
                      <FormControl className="po-label">
                        パートナー支払日
                      </FormControl>
                      <FormControl sx={{ width: "150px" }}>
                        <InputLabel id="serch-input-label">日</InputLabel>
                        <Select
                          labelId="serch-input-label"
                          id="serch-input"
                          value={getXLSXStatus.payment_day}
                          label={"日"}
                          onChange={(e) =>
                            setGetXLSXStatus({
                              ...getXLSXStatus,
                              payment_day: e.target.value,
                            })
                          }
                          sx={{
                            height: "30px",
                            textAlign: "end",
                          }}
                        >
                          {createDayList().map((input, i) => {
                            return (
                              <MenuItem
                                key={"input" + i}
                                value={input}
                                sx={{ textAlign: "right" }}
                              >
                                {input}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box
                      key={"paymentElsxSaleCompany"}
                      sx={{ margin: "30px 0px", display: "flex", flexDirection: "column", gap: "var(--sp-1)", paddingLeft: "30px", alignItems: "flex-start" }}
                    >
                      <FormControl className="po-label">
                        パートナー
                      </FormControl>
                      <FormControl sx={{ width: "100%", maxWidth: "380px" }}>
                        <Autocomplete
                          sx={{
                            height: "30px",
                            textAlign: "end",
                          }}
                          clearIcon={null}
                          options={autocompleteSaleCompanyList}
                          value={postCSVSaleCompanyName}
                          onChange={(event: any, newValue: string | null) => {
                            if (newValue) {
                              setPostCSVSaleCompanyName(newValue);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              value={postCSVSaleCompanyName}
                              sx={{
                                "& .MuiInputBase-root": {
                                  fontSize: "16px",
                                  height: "32px",
                                },
                              }}
                              {...params}
                            />
                          )}
                        />
                      </FormControl>
                    </Box>
                    {saleCompanyUniquePayments.length > 0 ? (
                      <Box
                        key={"paymentElsxsaleCompanyUniquePayments"}
                        sx={{ margin: "30px 0px" }}
                      >
                        <FormControl
                          sx={{
                            marginLeft: "30px",
                            marginTop: "10px",
                            marginBottom: "10px",
                            width: "700px",
                          }}
                        >
                          パートナー独自単価設定
                        </FormControl>
                        {saleCompanyUniquePayments.map(
                          (saleCompanyUniquePayment, index) => {
                            return (
                              <Box
                                className="po-label"
                                sx={{ marginRight: "25px", display: "flex" }}
                              >
                                <Checkbox
                                  checked={getXLSXStatus.saleCompanyUniquePaymentIDs.includes(
                                    saleCompanyUniquePayment.id
                                  )}
                                  sx={{
                                    marginLeft: "30px",
                                    marginTop: "5px",
                                    "& .MuiSvgIcon-root": { fontSize: 16 },
                                  }}
                                  onClick={() => {
                                    let _saleCompanyUniquePaymentIDs =
                                      getXLSXStatus.saleCompanyUniquePaymentIDs.concat();
                                    if (
                                      getXLSXStatus.saleCompanyUniquePaymentIDs.includes(
                                        saleCompanyUniquePayment.id
                                      )
                                    ) {
                                      _saleCompanyUniquePaymentIDs =
                                        _saleCompanyUniquePaymentIDs.filter(
                                          (_saleCompanyUniquePaymentID) =>
                                            _saleCompanyUniquePaymentID !==
                                            saleCompanyUniquePayment.id
                                        );
                                    } else {
                                      _saleCompanyUniquePaymentIDs.push(
                                        saleCompanyUniquePayment.id
                                      );
                                    }
                                    _saleCompanyUniquePaymentIDs.sort(
                                      (a, b) => Number(a) - Number(b)
                                    );
                                    let _saleCompanyUniquePayments: {
                                      id: string;
                                      sale_company_unique_payment_name: string;
                                      sale_company_unique_payment_value: string;
                                    }[] = [];
                                    saleCompanyUniquePayments.map(
                                      (_saleCompanyUniquePayment) => {
                                        if (
                                          _saleCompanyUniquePaymentIDs.includes(
                                            _saleCompanyUniquePayment.id
                                          )
                                        ) {
                                          _saleCompanyUniquePayments.push(
                                            _saleCompanyUniquePayment
                                          );
                                        }
                                      }
                                    );
                                    setGetXLSXStatus({
                                      ...getXLSXStatus,
                                      saleCompanyUniquePaymentIDs:
                                        _saleCompanyUniquePaymentIDs,
                                      saleCompanyUniquePayments:
                                        _saleCompanyUniquePayments,
                                    });
                                  }}
                                />
                                <Box
                                  sx={{
                                    marginLeft: "10px",
                                    marginTop: "10px",
                                    width: "300px",
                                  }}
                                >
                                  {
                                    saleCompanyUniquePayment.sale_company_unique_payment_name
                                  }
                                </Box>
                                <Box
                                  sx={{
                                    marginTop: "10px",
                                    width: "380px",
                                    textAlign: "right",
                                  }}
                                >
                                  <CustomTextField
                                    id="textFieldId"
                                    value={
                                      saleCompanyUniquePayment.sale_company_unique_payment_value
                                    }
                                    size="small"
                                    onChange={(e) => {
                                      let _saleCompanyUniquePayments =
                                        saleCompanyUniquePayments.concat();
                                      _saleCompanyUniquePayments =
                                        _saleCompanyUniquePayments.map(
                                          (_saleCompanyUniquePayment) => {
                                            if (
                                              _saleCompanyUniquePayment ==
                                              saleCompanyUniquePayment
                                            ) {
                                              return {
                                                ..._saleCompanyUniquePayment,
                                                sale_company_unique_payment_value:
                                                  e.target.value,
                                              };
                                            } else {
                                              return _saleCompanyUniquePayment;
                                            }
                                          }
                                        );
                                      setSaleCompanyUniquePayments(
                                        _saleCompanyUniquePayments
                                      );
                                      let __saleCompanyUniquePayments: {
                                        id: string;
                                        sale_company_unique_payment_name: string;
                                        sale_company_unique_payment_value: string;
                                      }[] = [];
                                      _saleCompanyUniquePayments.map(
                                        (_saleCompanyUniquePayment) => {
                                          if (
                                            getXLSXStatus.saleCompanyUniquePaymentIDs.includes(
                                              _saleCompanyUniquePayment.id
                                            )
                                          ) {
                                            __saleCompanyUniquePayments.push(
                                              _saleCompanyUniquePayment
                                            );
                                          }
                                        }
                                      );
                                      setGetXLSXStatus({
                                        ...getXLSXStatus,
                                        saleCompanyUniquePayments:
                                          __saleCompanyUniquePayments,
                                      });
                                    }}
                                    sx={{
                                      width: "360px",
                                      textAlign: "right",
                                      marginTop: "-5px",
                                    }}
                                  />
                                  円
                                </Box>
                              </Box>
                            );
                          }
                        )}
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </Box>
            </Paper>
          </Typography>
        ) : getXLSXStatus.status === "loading" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "var(--sp-2)",
              }}
            >
              {/* cmd-po_1248: 待ち表示スピナー。span.po-spinnerはinline=親flex化で32x32を効かせる(先例 Dashboard.tsx:269) */}
              <span className="po-spinner" data-testid="xlsx-loading-spinner" />
              <Box sx={{ textAlign: "center" }}>明細作成準備中</Box>
            </Box>
          </Typography>
        ) : getXLSXStatus.status === "getting" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "var(--sp-2)",
              }}
            >
              {/* cmd-po_1248: 待ち表示スピナー。span.po-spinnerはinline=親flex化で32x32を効かせる(先例 Dashboard.tsx:269) */}
              <span className="po-spinner" data-testid="xlsx-loading-spinner" />
              <Box sx={{ textAlign: "center" }}>{getXLSXStatus.message}</Box>
            </Box>
          </Typography>
        ) : getXLSXStatus.status === "error" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Alert severity="error">
              <Box>{getXLSXStatus.message || "パートナー明細出力に失敗しました。"}</Box>
            </Alert>
          </Typography>
        ) : getXLSXStatus.status === "confirm" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Box>作成した明細の値を確定し、パートナーに公開しますか？</Box>
          </Typography>
        ) : getXLSXStatus.status === "error_confirm" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Alert severity="error">
              <Box>パートナー明細の確定に失敗しました。</Box>
            </Alert>
          </Typography>
        ) : getXLSXStatus.status === "send_mail" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Box>作成した明細の値をに公開しました。</Box>
            <Box>パートナーアカウントに通知メールを送信しますか？</Box>
          </Typography>
        ) : getXLSXStatus.status === "error_send_mail" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Alert severity="error">
              <Box>パートナー明細の確定メール送信に失敗しました。</Box>
            </Alert>
          </Typography>
        ) : getXLSXStatus.status === "result" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0 }}>
            <Box>{getXLSXStatus.message}</Box>
          </Typography>
        ) : null}
    </PoModalShell>
  );
}
